import React, { useState, useEffect, useCallback } from 'react';
import { apiClient, API_CONFIG } from '../api/config';
import { Button, Card, Typography, Spin, Alert, Space } from 'antd';
import { logger } from '../utils/logger';
import activityTracker from '../user/ActivityTracker';
import { useCompany } from '../context/CompanyContext';
import { getFromCache, saveToCache } from '../utils/cacheUtils';
import { useLocation } from 'react-router-dom';

function App_company({ initialSymbol }) {
  const { companyInfo } = useCompany();
  const location = useLocation();

  const [companyData, setCompanyData] = useState({
    cik: null,
    loadingStage: 0, // 0: 未加载, 1: 抓取10K, 2: 抓取业务介绍, 3: 翻译中
    error: null,
    latestReport: null,
    itemContent: null, // 新增 item 内容状态
    isitemLoading: false, // 新增 item 加载状态
    loadingMessage: '正在載入公司經營績效內容...', // 新增加载消息状态
    formType: '', // 新增 formType 状态
    filingDate: '', // 新增 filingDate 状态
    // 添加新的状态
    lastClicked: null,
    item10KContent: null,
    item10QContent: null,
    is10KLoading: false,
    is10QLoading: false
  });

  // 添加一个状态来存储缓存数据
  const [cachedData, setCachedData] = useState(null);

  // 清除内容函数
  const clearContent = useCallback(() => {
    setCompanyData(prev => ({
      ...prev,
      error: null,
      loadingStage: 0
    }));
  }, []);

  // 在组件加载时从缓存读取数据
  useEffect(() => {
    if (!companyInfo?.symbol) return;
    
    // 记录页面访问
    activityTracker.trackPageView(companyInfo.symbol);
    
    const cacheKey = `companyPerfData_${companyInfo.symbol}`;
    const savedData = getFromCache(cacheKey);
    console.log('从缓存读取数据:', { cacheKey, savedData });
    
    if (savedData) {
      // 只有当保存的symbol与当前symbol匹配时才设置缓存数据
      if (savedData.symbol === companyInfo?.symbol) {
        setCachedData(savedData);
      } else {
        setCachedData(null);
        clearContent();
      }
    } else {
      console.log('未找到缓存数据');
      setCachedData(null);
    }
  }, [clearContent, companyInfo?.symbol, location.pathname]);

  // 使用另一个 useEffect 来更新 companyData
  useEffect(() => {
    if (cachedData) {
      console.log('准备从缓存数据更新状态:', {
        hasItemContent: !!cachedData.itemContent,
        formType: cachedData.formType,
        filingDate: cachedData.filingDate
      });
      
      setCompanyData(prev => ({
        ...prev,
        symbol: cachedData.symbol,
        itemContent: cachedData.itemContent || null,
        formType: cachedData.formType || '',
        filingDate: cachedData.filingDate || '',
        isitemLoading: false
      }));
    }
  }, [cachedData]);

  // 监听 companyData 的变化
  useEffect(() => {
    console.log('companyData 发生变化:', {
      hasItemContent: !!companyData.itemContent,
      contentLength: companyData.itemContent?.length,
      formType: companyData.formType,
      filingDate: companyData.filingDate,
      isitemLoading: companyData.isitemLoading
    });
  }, [companyData]);

  // 添加加载消息的 useEffect
  useEffect(() => {
    let timer1, timer2;
    
    if (companyData.isitemLoading) {
      // 3秒后显示"翻译中"
      timer1 = setTimeout(() => {
        setCompanyData(prev => ({
          ...prev,
          loadingMessage: '正在翻譯內容...'
        }));
      },8000);

      // 6秒后显示"文字较多请耐心等待"
      timer2 = setTimeout(() => {
        setCompanyData(prev => ({
          ...prev,
          loadingMessage: '資料較多, 請耐心等待...'
        }));
      }, 16000);
    }

    // 清理定时器
    return () => {
      if (timer1) clearTimeout(timer1);
      if (timer2) clearTimeout(timer2);
    };
  }, [companyData.isitemLoading]);

  // 修改处理获取 item 的点击事件
  const handleGetItem = async (formType) => {
    if (!companyInfo?.symbol) {
      logger.warn('未选择公司，无法获取 Item 内容');
      setCompanyData(prev => ({
        ...prev,
        error: '請先在搜索框中選擇公司'
      }));
      return;
    }

    logger.info('开始处理请求:', { formType, symbol: companyInfo.symbol });

    // 添加活动追踪
    activityTracker.trackBtnExecution(
      formType === '10-K' ? 'AI_latest_10k' : 'AI_latest_10q',
      companyInfo.symbol,
      window.location.pathname
    );

    // 清空现有内容并显示加载状态
    setCompanyData(prev => {
      const newState = {
        ...prev,
        isitemLoading: true,
        error: null,
        loadingMessage: '正在載入公司經營績效內容...',
        lastClicked: formType,
        itemContent: null,
        formType: '',
        filingDate: ''
      };
      return newState;
    });

    try {
      logger.info(`请求 item API: ${API_CONFIG.endpoints.itemsShow}?symbol=${companyInfo.symbol}&formtype=${formType}`);
      const response = await apiClient.get(
        `${API_CONFIG.endpoints.itemsShow}?symbol=${companyInfo.symbol}&formtype=${formType}`
      );
      
      if (response.data.error) {
        throw new Error(response.data.error);
      }

      // 處理 HTML 內容
      const processedContent = response.data.content
        // 處理表格樣式
        .replace(/<table[^>]*>/g, (match) => {
          // 設置新的樣式
          const newStyle = 'border-collapse: collapse; margin-bottom: 20px;';
          // 返回新的表格標籤，不包含原始樣式
          return `<table class="table table-bordered" style="${newStyle}">`;
        })
        .replace(/<tr[^>]*>/g, (match) => {
          // 設置新的樣式
          const newStyle = 'border-bottom: 1px solid #dee2e6;';
          return `<tr class="table-row" style="${newStyle}">`;
        })
        .replace(/<td[^>]*>/g, (match) => {
          // 保留原有的 colspan 和 rowspan 屬性
          const colspan = match.match(/colspan="(\d+)"/);
          const rowspan = match.match(/rowspan="(\d+)"/);
          
          // 設置新的樣式，包含防止數字換行的樣式
          const newStyle = 'padding: 8px; vertical-align: middle; border: 1px solid #dee2e6; word-wrap: break-word; overflow-wrap: break-word;';
          
          let newMatch = '<td class="table-cell" style="' + newStyle + '"';
          if (colspan) newMatch += ` colspan="${colspan[1]}"`;
          if (rowspan) newMatch += ` rowspan="${rowspan[1]}"`;
          newMatch += '>';
          
          return newMatch;
        })
        // 處理 span 標籤
        .replace(/<span[^>]*>/g, '')
        .replace(/<\/span>/g, '')
        // 處理換行標籤
        .replace(/<br\/>/g, '<br>')
        // 處理數字格式化 - 包含小數點
        .replace(/\$(\d+,\d+|\d+\.\d+|\d+)\s/g, (match, num) => {
          return `<span style="color: #28a745; text-align: right; display: inline-block; width: 100%; white-space: nowrap;">${match}</span>`;
        })
        // 處理百分比 - 包含小數點
        .replace(/(\d+\.\d+|\d+)\s*%/g, (match, num) => {
          const color = parseFloat(num) >= 0 ? '#28a745' : '#dc3545';
          return `<span style="color: ${color}; text-align: right; display: inline-block; width: 100%; white-space: nowrap;">${match}</span>`;
        })
        // 處理純數字 - 包含小數點
        .replace(/(\d+,\d+|\d+\.\d+|\d+)\s/g, (match, num) => {
          // 檢查是否已經被其他規則處理過
          if (!match.includes('$') && !match.includes('%')) {
            return `<span style="text-align: right; display: inline-block; width: 100%; white-space: nowrap;">${match}</span>`;
          }
          return match;
        })
        // 處理標題樣式
        .replace(/([^\n]+)\n\n/g, (match, text) => {
          const chineseCharCount = (text.match(/[\u4e00-\u9fa5]/g) || []).length;
          if (chineseCharCount < 18 && chineseCharCount > 0) {
            return `<strong style="font-size: 1.1em; color: #333;">${text}</strong>\n\n`;
          }
          return match;
        })
        // 處理段落樣式
        .replace(/<p/g, '<p style="margin-bottom: 1rem; line-height: 1.6;"')
        // 處理列表樣式
        .replace(/<ul/g, '<ul style="margin-bottom: 1rem; padding-left: 2rem;"')
        .replace(/<ol/g, '<ol style="margin-bottom: 1rem; padding-left: 2rem;"')
        .replace(/<li/g, '<li style="margin-bottom: 0.5rem;"');

      // 更新状态
      setCompanyData(prev => {
        const newState = {
          ...prev,
          itemContent: processedContent,
          isitemLoading: false,
          formType: response.data.formType || '',
          filingDate: response.data.filingDate || ''
        };

        // 保存到缓存
        const cacheKey = `companyPerfData_${companyInfo.symbol}`;
        const cacheData = {
          symbol: companyInfo.symbol,
          itemContent: newState.itemContent,
          formType: newState.formType,
          filingDate: newState.filingDate,
          lastClicked: formType
        };
        console.log('保存到缓存:', { 
          cacheKey, 
          hasItemContent: !!cacheData.itemContent,
          contentLength: cacheData.itemContent?.length
        });
        saveToCache(cacheKey, cacheData);

        return newState;
      });
      
    } catch (error) {
      logger.error('获取 item 内容失败:', error);
      logger.error('错误详情:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      let errorMessage = error.message;
      
      // 處理 404 錯誤
      if (error.response && error.response.status === 404) {
        // 檢查不同格式的響應數據
        const responseData = error.response.data;
        
        if (typeof responseData === 'string' && responseData.includes('未找到数据')) {
          errorMessage = '未找到數據';
        } else if (responseData && typeof responseData === 'object') {
          // 如果響應是對象，檢查常見的錯誤字段
          if (responseData.error && responseData.error.includes('未找到数据')) {
            errorMessage = responseData.message;
          } else {
            // 如果是 404 但沒有特定錯誤訊息，使用通用訊息
            errorMessage = '未找到數據';
          }
        } else {
          // 對於所有 404 錯誤，都顯示友好訊息
          errorMessage = '未找到數據';
        }
      }
      
      setCompanyData(prev => ({
        ...prev,
        error: errorMessage,
        isitemLoading: false
      }));
    }
  };

  return (
    <div className="company-search">
      <div className="content-container">
  
        <form className="search-form">
          <div className="form-header1" style={{ display: 'flex', alignItems: 'center', gap: '10px'}}>
            
            
            <Button 
              type="primary"
              onClick={() => handleGetItem('10-K')}
              loading={companyData.isitemLoading}
              style={{ 
                fontSize: '14px'
              }}
            >
              擷取最新年報陳述
            </Button>

            <Button 
              type="primary"
              onClick={() => handleGetItem('10-Q')}
              loading={companyData.isitemLoading}
              style={{ 
                fontSize: '14px'
              }}
            >
              擷取最新季報陳述
            </Button>
          </div>
          <br></br>
          <div className="form-header1" style={{ display: 'flex', alignItems: 'center', gap: '40px'}}></div>
          <label style={{ fontSize: '14px' }}>
               只擷取和公司經營績效與分析討論相關的內容，若沒有顯示 請到「財報文件」頁面中查看
            </label>
          <hr />
        </form>

        {/* 内容显示区块 */}
        {companyData.itemContent && (
          <Card style={{ marginTop: '20px' }}>
            <Typography.Title level={3} style={{ textAlign: 'center', marginBottom: '16px' }}>
              {companyData.lastClicked === '10-K' ? '10-K 經營績效討論與分析' : '10-Q 經營績效討論與分析'}
            </Typography.Title>
            <Space direction="horizontal" size="large" style={{ 
              marginBottom: '16px',
              width: '100%',
              justifyContent: 'center'
            }}>
              <Typography.Text type="secondary">
                文件類型: {companyData.formType || '未知'}
              </Typography.Text>
              <Typography.Text type="secondary">
                申報日期: {companyData.filingDate ? new Date(companyData.filingDate).toLocaleDateString() : '未知'}
              </Typography.Text>
              <Typography.Text type="secondary">
                完整文件資料 請查看「歷史文件」
              </Typography.Text>
            </Space>
            <div 
              style={{ 
                whiteSpace: 'pre-line',
                padding: '16px',
                backgroundColor: '#fafafa',
                borderRadius: '6px',
                border: '1px solid #f0f0f0'
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: companyData.itemContent }} />
            </div>
          </Card>
        )}

        {companyData.isitemLoading && (
          <Card style={{ marginTop: '20px', textAlign: 'center' }}>
            <Spin size="large" />
            <Typography.Text style={{ display: 'block', marginTop: '16px' }}>
              {companyData.loadingMessage}
            </Typography.Text>
          </Card>
        )}

        {companyData.error && (
          <Alert
            message="錯誤"
            description={companyData.error}
            type="error"
            showIcon
            style={{ marginTop: '20px' }}
          />
        )}
      </div>

      <style jsx>{`
        .search-form {
          margin-top: 20px;  /* 添加上方空间 */
          margin-bottom: 20px;
        }
        .table {
          width: 100%;
          margin-bottom: 1rem;
          color: #212529;
          border-collapse: collapse;
        }
        .table-bordered {
          border: 1px solid #dee2e6;
        }
        .table-row {
          border-bottom: 1px solid #dee2e6;
        }
        .table-cell {
          padding: 0.75rem;
          vertical-align: top;
          border: 1px solid #dee2e6;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        /* 防止数字换行 */
        .table-cell span {
          white-space: nowrap;
          word-break: keep-all;
        }
        .content-area {
          font-family: Arial, sans-serif;
          line-height: 1.6;
        }
        .error-message {
          color: #dc3545;
          padding: 10px;
          margin: 10px 0;
          border: 1px solid #dc3545;
          border-radius: 4px;
          background-color: #f8d7da;
        }
        .loading-message {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 20px;
          margin: 20px 0;
        }
        .loading-spinner {
          border: 4px solid #f3f3f3;
          border-top: 4px solid #3498db;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
          margin-bottom: 10px;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @media screen and (max-width: 768px) {
          .form-header1 label {
            font-size: 14px !important;
          }
          .form-header1 label:last-child {
            font-size: 11px !important;
          }
          .content-area {
            font-size: 14px;
            padding: 15px;
          }
          .content-header h3 {
            font-size: 16px;
          }
        }
        @media screen and (max-width: 480px) {
          .form-header1 label {
            font-size: 12px !important;
          }
          .form-header1 label:last-child {
            font-size: 10px !important;
          }
          .content-area {
            font-size: 12px;
            padding: 10px;
            line-height: 1.4;
          }
          .content-header h3 {
            font-size: 14px;
          }
          .table-cell {
            padding: 0.5rem;
            font-size: 12px;
          }
        }
      `}</style>
    </div>
  );
}

export default App_company; 